import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Loader2, ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${API_BASE}/api`;

/**
 * ProposalDeckViewer — landscape (16:9) slide-deck preview.
 *
 * Renders the exact PNG-per-slide output from the assembled PPTX so what a
 * rep sees in the portal matches the downloaded deck pixel-for-pixel. Loads
 * lazily: hitting the /slide-previews endpoint triggers a LibreOffice render
 * on the server which caches the result in Mongo — subsequent viewers just
 * fetch cached PNGs.
 *
 * Keyboard: ArrowRight / ArrowLeft advances, Escape exits fullscreen.
 * Click a thumbnail to jump. Click the main slide (in Present mode) to
 * advance.
 *
 * data-testid: `deck-viewer`, `deck-slide-{idx}`, `deck-present-btn`,
 * `deck-prev-btn`, `deck-next-btn`, `deck-exit-fullscreen-btn`
 */
export default function ProposalDeckViewer({ propId, updatedAt }) {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [assetIds, setAssetIds] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Lazy-load previews on mount and whenever the proposal is re-generated
  useEffect(() => {
    if (!propId) return;
    let cancelled = false;
    setStatus("loading");
    axios.get(`${API}/prospect-proposals/${propId}/slide-previews`)
      .then(r => {
        if (cancelled) return;
        const ids = r.data?.asset_ids || [];
        setAssetIds(ids);
        setStatus(ids.length ? "ready" : "error");
        setCursor(0);
      })
      .catch(() => { if (!cancelled) setStatus("error"); });
    return () => { cancelled = true; };
  }, [propId, updatedAt]);

  const goNext = useCallback(() => setCursor(c => Math.min(assetIds.length - 1, c + 1)), [assetIds.length]);
  const goPrev = useCallback(() => setCursor(c => Math.max(0, c - 1)), []);

  // Keyboard shortcuts (fullscreen + baseline)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "Escape" && fullscreen) setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, fullscreen]);

  const enterFullscreen = () => {
    setFullscreen(true);
    // Best-effort real browser fullscreen — falls back to CSS-only if the API
    // is blocked (works in iframes / sandboxed contexts).
    try { containerRef.current?.requestFullscreen?.(); } catch { /* ignore */ }
  };
  const exitFullscreen = () => {
    setFullscreen(false);
    try { if (document.fullscreenElement) document.exitFullscreen(); } catch { /* ignore */ }
  };

  if (status === "loading") {
    return (
      <div className="border rounded-lg bg-slate-50 p-6 text-center" data-testid="deck-viewer-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-fuchsia-600 mb-2" />
        <p className="text-sm text-slate-600">Rendering deck preview — this takes ~15-25 seconds the first time.</p>
      </div>
    );
  }
  if (status === "error" || assetIds.length === 0) {
    return (
      <div className="border rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
        Couldn&apos;t render the slide preview. Download the PPTX/PDF to view the deck.
      </div>
    );
  }

  const cur = assetIds[cursor];
  const slideUrl = (id) => `${API}/mockup-assets/${id}`;

  const viewer = (
    <div ref={containerRef}
         className={fullscreen
            ? "fixed inset-0 z-[1000] bg-black flex flex-col"
            : "border rounded-lg bg-slate-900 overflow-hidden"}
         data-testid="deck-viewer">
      <div className="flex-1 flex items-center justify-center relative">
        <img
          src={slideUrl(cur)}
          alt={`Slide ${cursor + 1}`}
          data-testid={`deck-slide-${cursor}`}
          className={fullscreen
            ? "max-h-[100vh] max-w-[100vw] object-contain cursor-pointer"
            : "w-full aspect-video object-contain cursor-pointer"}
          onClick={goNext}
          onError={() => toast.error("Slide image failed to load")}
        />
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          data-testid="deck-prev-btn"
          disabled={cursor === 0}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 disabled:opacity-30"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          data-testid="deck-next-btn"
          disabled={cursor >= assetIds.length - 1}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 disabled:opacity-30"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {fullscreen ? (
            <button
              onClick={exitFullscreen}
              data-testid="deck-exit-fullscreen-btn"
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
              title="Exit presentation (Esc)"
            >
              <X className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={enterFullscreen}
              data-testid="deck-present-btn"
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1"
              title="Present fullscreen"
            >
              <Maximize2 className="h-3.5 w-3.5" /> Present
            </button>
          )}
        </div>
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs font-mono px-2 py-1 rounded">
          {cursor + 1} / {assetIds.length}
        </div>
      </div>

      {!fullscreen && (
        <div className="bg-slate-800 px-3 py-2 flex gap-1 overflow-x-auto" data-testid="deck-thumb-rail">
          {assetIds.map((id, i) => (
            <button
              key={id}
              onClick={() => setCursor(i)}
              data-testid={`deck-thumb-${i}`}
              className={`shrink-0 border-2 transition-all rounded-sm overflow-hidden ${i === cursor ? "border-fuchsia-500 opacity-100" : "border-transparent opacity-60 hover:opacity-90"}`}
            >
              <img src={slideUrl(id)} alt={`Thumb ${i + 1}`} className="h-12 w-auto" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return viewer;
}
